import type { ReactNode } from "react";

import { PRIVACY_CHIP_DEFAULT_LABEL } from "./privacyChipCopy";

// PrivacyChip — "Private to you" privacy marker. A quiet hairline pill: leading
// lock glyph + uppercase tracked label. Standalone (not a Chip variant)
// rather than a Chip variant, because privacy is not a status role and Chip's
// role×variant matrix is sentence-case, not this fixed uppercase-tracked+glyph
// treatment. Pervasive on private couple surfaces (Homebase) and lifted from the
// Therapist Portal's CONFIDENTIAL pill — pass `children` to relabel (default
// "Private to you"; the portal passes "Confidential"). DESIGN.md §3 (quiet
// marker, not a status fill).

export interface PrivacyChipProps {
    /** Marker label, rendered uppercase + tracked. Defaults to "Private to you"
     *  (Homebase); the portal's confidential pill passes "Confidential". */
    children?: ReactNode;
    className?: string;
}

// Padlock — closed shackle over a rounded body. Decorative (the label carries
// the meaning), so aria-hidden; strokes inherit the pill's currentColor.
function LockGlyph() {
    return (
        <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="shrink-0"
        >
            <rect
                x="3.25"
                y="7"
                width="9.5"
                height="6.5"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.4"
            />
            <path
                d="M5.5 7V5.25a2.5 2.5 0 0 1 5 0V7"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function PrivacyChip({ children = PRIVACY_CHIP_DEFAULT_LABEL, className }: PrivacyChipProps) {
    return (
        <span
            className={[
                "inline-flex items-center gap-1 rounded-[var(--radius-pill)] border border-[var(--border)]",
                "bg-[var(--surface)] px-2 py-0.5",
                "[font:var(--type-eyebrow)] uppercase [letter-spacing:var(--tracking-eyebrow)] text-[var(--text-secondary)]",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <LockGlyph />
            {children}
        </span>
    );
}
